var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import makeStyles from "@mui/styles/makeStyles";
import produce from "immer";
import { addUpdateAccountDeliveryAddress } from "../Account/utils/requests";
import { getAddressesAndQuestions } from "../Account/utils/requestsTS";
import { ERROR_MESSAGE_GENERIC } from "../Shared/utils/constants";
import { AddressesText, deliveryQuestionsFieldNamePrefix, } from "./Addresses-types";
import { getDeliveryDates } from "../Shared/components/AddressCard/utils/addressCardUtils";
import { checkCurrentOrderPlaceChange } from "../Account/useUpdateOrderDeliveryModal/deliveryModalUtils/checkCurrentOrderPlaceChange";
import { ActionTypes } from "../OrderFlow/OrderFlowProvider/OrderFlowActionTypes";
// ********** STYLES
/**
 * This is necessary to counter the style overrides that the mapbox autofill component imposes.
 */
export const useAddressStyles = makeStyles({
    root: {
        "& .MuiInputBase-input": {
            boxSizing: "content-box !important",
        },
    },
});
export const baseCardStyles = { color: "black" };
// ********** HELPER FUNCTIONS
/**
 * Set any saved answers as the form default's value or pass an empty string.
 * @param deliveryQuestionsMap
 * @param address
 * @returns
 */
export const defaultDeliveryQuestionsAnswer = (deliveryQuestionsMap, currentCardAddress) => Object.fromEntries(deliveryQuestionsMap.map((q) => {
    var _a, _b;
    return [
        deliveryQuestionFieldName(q),
        (_b = (_a = currentCardAddress === null || currentCardAddress === void 0 ? void 0 : currentCardAddress.deliveryAnswers.find((ans) => ans.questionId === q.QuestionId)) === null || _a === void 0 ? void 0 : _a.answer) !== null && _b !== void 0 ? _b : "",
    ];
}));
/**
 * Takes the "checkout" questions and filters for applicable delivery questions.
 * @param questions
 * @returns delivery questions
 */
export const buildQuestionsMap = (questions) => questions.filter((q) => !!q.IsDelivery);
/**
 * Sorts addresses by four criteria: primary address first, then by state, city, and finally addressee's first name.
 * @param a DeliveryAddressType
 * @param b DeliveryAddressType
 * @returns sorted values
 */
export const addressCardSortingFunction = (a, b) => {
    if (a.isPrimary)
        return -1;
    if (b.isPrimary)
        return 1;
    if (a.state === b.state && a.city === b.city && a.firstName && b.firstName) {
        return a.firstName.localeCompare(b.firstName, undefined, { sensitivity: "base" });
    }
    if (a.state === b.state) {
        return a.city.localeCompare(b.city, undefined, { sensitivity: "base" });
    }
    return a.state.localeCompare(b.state, undefined, { sensitivity: "base" });
};
/**
 *
 * @param a DeliveryAddressesAndQuestions
 * @param b DeliveryAddressesAndQuestions
 * @returns sorted addresses
 */
const deliverySorter = (a, b) => {
    if (a.state === b.state && a.city === b.city && a.firstName && b.firstName) {
        return a.firstName.localeCompare(b.firstName, undefined, { sensitivity: "base" });
    }
    if (a.state === b.state) {
        return a.city.localeCompare(b.city, undefined, { sensitivity: "base" });
    }
    return a.state.localeCompare(b.state, undefined, { sensitivity: "base" });
};
/**
 *
 * @param initDeliveryLocation
 * @param addresses
 * @returns sorted addresses with selected (if applicable) first, then primary.
 */
export const deliveryAddressSortingFunction = (initDeliveryLocation, addresses) => {
    if (!addresses)
        return [];
    const currentSelection = addresses.find((a) => String(a.addressId) === initDeliveryLocation);
    const primaryAddress = addresses.find((a) => a.isPrimary);
    const otherAddresses = addresses
        .filter((b) => String(b.addressId) !== initDeliveryLocation)
        .filter((a) => !a.isPrimary);
    const sortedAddresses = otherAddresses.sort(deliverySorter);
    if (primaryAddress && primaryAddress.addressId !== (currentSelection === null || currentSelection === void 0 ? void 0 : currentSelection.addressId))
        sortedAddresses.unshift(primaryAddress);
    if (currentSelection)
        sortedAddresses.unshift(currentSelection);
    return sortedAddresses;
};
/**
 * Sorts pickup locations by name.
 * @param a PickupLocationsInterface
 * @param b PickupLocationsInterface
 * @returns sorted locations
 */
const pickupSorter = (a, b) => +a.locationSeq - +b.locationSeq;
/**
 *
 * @param initPickupLocation
 * @param pickupLocations
 * @returns sorted locations with selected (if applicable) first.
 */
export const pickupAddressSortingFunction = (initPickupLocation, pickupLocations) => {
    const currentSelection = pickupLocations.find((a) => String(a.locationSku) === initPickupLocation);
    const otherLocations = pickupLocations.filter((p) => p.locationSku !== initPickupLocation);
    const sortedLocations = otherLocations.sort(pickupSorter);
    if (currentSelection)
        sortedLocations.unshift(currentSelection);
    return sortedLocations;
};
/**
 * Sorts delivery questions by question number.
 * @param a
 * @param b
 * @returns sorted questions
 */
export const deliveryQuestionsSortingFunction = (a, b) => a.QuestionNumber - b.QuestionNumber;
/**
 * Address success handler.
 * @NOTE A success does not imply that the address update was successful, only that the request was successful,
 * which is why we still have error handling in the success response.
 * @param closeModal
 * @param toast
 * @param isUpdate
 * @param refetchAddresses
 * @ void
 */
export const onAddressesUpdateSuccess = (closeModal, toast, isUpdate, refetchAddresses, setError, setReactHookFormValue, reset, setCheckoutFormValue) => (res) => {
    if (!res.data.success && res.data.message === "Bad zip.") {
        /**
         * If we get a bad zip response, we set a form error for the zip field,
         * set and display an error toast, and set the isPrimary field to false.
         */
        setError("zip", { type: "custom", message: AddressesText.BAD_ZIP_FIELD_MSG });
        toast.push(AddressesText.BAD_ZIP_TOAST, { variant: "error" });
        setReactHookFormValue("isPrimary", false);
    }
    else {
        toast.push(isUpdate ? AddressesText.ADDRESS_UPDATED : AddressesText.ADDRESS_ADDED, { variant: "success" });
        if (setCheckoutFormValue) {
            // This is coming from Checkout flow. set the form address value to the newly created address ID.
            setCheckoutFormValue("address", res.data.addressId);
            setCheckoutFormValue("location", undefined);
        }
        else {
            // This is coming from Account page. close modal and reset the form.
            closeModal();
            reset();
        }
        refetchAddresses();
    }
};
/**
 * Error handler
 * @param toast
 * @returns void
 */
export const onAddressesUpdateError = (toast) => () => {
    toast.push(ERROR_MESSAGE_GENERIC, { variant: "error" });
};
/**
 * Collects disparate values into a single array of answers.
 * @param addressId
 * @param obj
 * @param answer
 * @returns array of delivery answers
 */
const buildDeliveryAnswerObj = (addressId, obj, answer) => ({
    addressId: addressId,
    questionId: obj.QuestionId,
    question: obj.Question,
    answer: answer,
    QuestionSKU: obj.QuestionSKU,
});
/**
 * Func to consistently name React Hook Form delivery answers form value.
 * @param q DeliveryQuestionType.
 * @returns Stringed question field name. E.g., "delivery-questions-notes", where "notes" is the question SKU.
 */
export const deliveryQuestionFieldName = (q) => `${deliveryQuestionsFieldNamePrefix}-${q.QuestionSKU}`;
/**
 * Collects the string keys from the form values object; helps shape the submission data.
 * @param data The submitted form values.
 * @returns The string keys from the form values object.
 */
const getAnswersKeys = (data) => Object.keys(data).filter((key) => key.startsWith(deliveryQuestionsFieldNamePrefix));
/**
 * For ease of use with RHF, we collect only the answers in the form.
 * When submitting the form, we shape the data to include the rest of the required question data for the answer object.
 * @param data
 * @param deliveryQuestionsMap
 * @returns
 */
export const shapeDeliveryAnswers = (data, deliveryQuestionsMap) => {
    const temp = getAnswersKeys(data).filter((key) => !!data[key]);
    return temp.map((t) => {
        const questionObj = deliveryQuestionsMap.find((q) => deliveryQuestionFieldName(q) === t);
        // Only create and return an answer object if the user has entered a response.
        if (questionObj && data[t])
            return buildDeliveryAnswerObj(data.addressId, questionObj, data[t]);
    });
};
/**
 * Combines the shaped delivery answers with the rest of the form data.
 * @param data Raw form data.
 * @param deliveryAnswers The delivery answers.
 */
const shapeSubmissionData = (data, deliveryAnswers) => {
    const finalValues = produce(data, (draft) => {
        const keysToRemove = getAnswersKeys(draft);
        keysToRemove.forEach((key) => {
            delete draft[key];
        });
        draft.deliveryAnswers = deliveryAnswers !== null && deliveryAnswers !== void 0 ? deliveryAnswers : [];
    });
    return finalValues;
};
/**
 * Data submission func.
 * @param deliveryQuestionsMap Mapped delivery questions.
 * @param onUpdateSuccess Success handler func.
 * @param onUpdateError Error handler func.
 * @returns void / submission results
 */
export const handleAddUpdateAddressFormSubmission = (deliveryQuestionsMap, onUpdateSuccess, onUpdateError, orderInfo, ShowDeliveryScheduleWarningModal, flowSku, dispatch, setCheckoutFormValue) => (data) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const deliveryAnswers = shapeDeliveryAnswers(data, deliveryQuestionsMap);
    const finalData = shapeSubmissionData(data, deliveryAnswers);
    //If on checkout, we handle the submission a bit differently so the order summary can be updated and zip checked for potential remapping
    if (setCheckoutFormValue) {
        const address = {
            zip: data.zip,
        };
        //if zip is different check if needs to be remapped,
        if ((orderInfo === null || orderInfo === void 0 ? void 0 : orderInfo.ZipCode) !== address.zip) {
            const currentOrderDeliveryDates = getDeliveryDates(orderInfo === null || orderInfo === void 0 ? void 0 : orderInfo.days);
            const response = yield checkCurrentOrderPlaceChange(currentOrderDeliveryDates, (_a = orderInfo === null || orderInfo === void 0 ? void 0 : orderInfo.program_id) !== null && _a !== void 0 ? _a : -1, address.zip, null);
            if (response.isRescheduleRequired || !response.success) {
                if (ShowDeliveryScheduleWarningModal) {
                    ShowDeliveryScheduleWarningModal(address, undefined, flowSku, true);
                    addUpdateAccountDeliveryAddress(finalData, () => { }, onUpdateError);
                    return;
                }
            }
            else {
                if (dispatch) {
                    dispatch({ type: ActionTypes.SET_POSTAL_CODE, payload: address.zip });
                    dispatch({ type: ActionTypes.SET_LOCATION, payload: undefined });
                }
            }
        }
    }
    addUpdateAccountDeliveryAddress(finalData, onUpdateSuccess, onUpdateError);
});
export const handleAddNewAddressClick = (showAddUpdateAddressModal, refetchAddresses, deliveryQuestions, geoLocationCoords) => () => showAddUpdateAddressModal(refetchAddresses, deliveryQuestions, geoLocationCoords);
export const initDeliveryAddressState = {
    addresses: [],
    pickupLocations: [],
    loading: true,
    loadingAddresses: true,
    loadingError: false,
    deliveryQuestions: [],
    geoLocationCoords: [0, 0],
    customerCreditCards: [],
    orderId: undefined,
    usesLocation: false,
    usesZipCodes: false,
    primaryAddressId: undefined,
    primaryAddressOrdersData: null,
    subscriptionActiveOrdersData: null,
    error: undefined,
};
export const refetchAddresses = (setState, programId, programSKU, orderId, initDeliveryAddressState, setCustomerShippingAddresses, subscriptionLinkId) => () => {
    setState((prev) => (Object.assign(Object.assign({}, prev), { loadingAddresses: true })));
    getAddressesAndQuestions(setState, programId, programSKU, orderId, initDeliveryAddressState, setCustomerShippingAddresses, subscriptionLinkId);
};
